/**
 * Request-flow editor layout shell.
 * Renders the validation/notification banners, main DBQ form, musculoskeletal supplemental
 * section insertion, and the sidebar review panel.
 */
import React from 'react';
import { Alert, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faComments, faLightbulb, faSpinner } from '@fortawesome/free-solid-svg-icons';

import { DbqFieldReviewPanel, DbqFormView } from '../index';
import DbqMusculoskeletalSupplementalSection from '../DbqMusculoskeletalSupplementalSection';

const UserDbqEditorRequestFlowLayout = ({
  formJson,
  showReviewPanel,
  validationPanelProps = {},
  notificationProps = {},
  isCaseManagerProviderReviewLocked = false,
  formViewProps = {},
  supplementalSectionRef = null,
  supplementalProps = {},
  reviewPanelProps = null,
}) => {
  const {
    missingRequiredFieldsList = [],
    onDismissMissingRequiredFields,
    onGoToMissingField,
  } = validationPanelProps;
  const {
    totalCommentCount = 0,
    shouldShowSuggestionNotificationCount = false,
    onCommentsNotificationClick,
    onSuggestionsNotificationClick,
    isSuggestionCountLoading = false,
    displayedSuggestionCount = 0,
  } = notificationProps;
  const {
    isMusculoskeletal = false,
    supplementalInsertAfterSectionId = null,
    displayedMusculoskeletalSupplementalData,
    onSupplementalChange,
    canEditMusculoskeletalSupplemental = false,
    editLocked = false,
    supplementalValidationErrors = [],
    activeReviewFieldId = null,
    canOpenReview = false,
    getReviewItemCount,
    onReviewClick,
    getInlineSuggestionStateForField,
    onOpenInlineSuggestionDetail,
    onOpenInlineSuggestionEvidence,
    onOpenInlineGenerationFailure,
    onOpenInlineRejectedSuggestions,
  } = supplementalProps;

  return (
    <div className="dbq-renderer">
      <div className={`dbq-layout-wrapper ${showReviewPanel ? 'has-comments' : ''}`}>
        <div className="dbq-form-container">
          <div className="dbq-header">
            {formJson?.description ? <p>{formJson.description}</p> : null}

            {missingRequiredFieldsList.length > 0 ? (
              <div className="dbq-validation-panel">
                <div className="dbq-validation-panel-header">
                  <div className="dbq-validation-panel-title">
                    <span className="dbq-validation-icon">!</span>
                    <span>
                      {missingRequiredFieldsList.length} required field{missingRequiredFieldsList.length !== 1 ? 's' : ''} still need{missingRequiredFieldsList.length === 1 ? 's' : ''} an answer
                    </span>
                  </div>
                  <button
                    type="button"
                    className="dbq-validation-panel-close"
                    onClick={() => onDismissMissingRequiredFields?.([])}
                    aria-label="Dismiss"
                  >
                    &times;
                  </button>
                </div>
                <div className="dbq-validation-panel-body">
                  {Object.entries(
                    missingRequiredFieldsList.reduce((grouped, field) => {
                      const section = field.sectionLabel || 'Other';
                      if (!grouped[section]) grouped[section] = [];
                      grouped[section].push(field);
                      return grouped;
                    }, {}),
                  ).map(([sectionLabel, fields]) => (
                    <div key={sectionLabel} className="dbq-validation-section">
                      <div className="dbq-validation-section-label">{sectionLabel}</div>
                      <div className="dbq-validation-field-list">
                        {fields.map((field, index) => (
                          <button
                            key={`${field.id}-${field.optionId || ''}-${index}`}
                            type="button"
                            className="dbq-validation-field-item"
                            onClick={() => onGoToMissingField?.(field)}
                          >
                            <span className="dbq-validation-field-dot" />
                            <span className="dbq-validation-field-label">{field.label}</span>
                            <span className="dbq-validation-field-action">Go to field</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            {(totalCommentCount > 0 || shouldShowSuggestionNotificationCount) && !showReviewPanel ? (
              <div className="dbq-review-notification-bar">
                {totalCommentCount > 0 ? (
                  <button
                    type="button"
                    className="dbq-review-notification-item dbq-review-notification-comments"
                    onClick={onCommentsNotificationClick}
                  >
                    <FontAwesomeIcon icon={faComments} className="dbq-review-notification-icon" />
                    <span className="dbq-review-notification-count">{totalCommentCount}</span>
                    <span className="dbq-review-notification-label">
                      Comment{totalCommentCount !== 1 ? 's' : ''}
                    </span>
                  </button>
                ) : null}
                {shouldShowSuggestionNotificationCount ? (
                  <button
                    type="button"
                    className="dbq-review-notification-item dbq-review-notification-suggestions"
                    onClick={onSuggestionsNotificationClick}
                  >
                    <FontAwesomeIcon icon={faLightbulb} className="dbq-review-notification-icon" />
                    <span
                      className="dbq-review-notification-count"
                      aria-label={isSuggestionCountLoading ? 'Suggestions loading' : undefined}
                      title={isSuggestionCountLoading ? 'Suggestions are still loading' : undefined}
                    >
                      {isSuggestionCountLoading ? (
                        <FontAwesomeIcon icon={faSpinner} spin />
                      ) : (
                        displayedSuggestionCount
                      )}
                    </span>
                    <span className="dbq-review-notification-label">
                      Suggestion{displayedSuggestionCount !== 1 ? 's' : ''}
                    </span>
                  </button>
                ) : null}
              </div>
            ) : null}

            {isCaseManagerProviderReviewLocked ? (
              <Alert variant="warning" className="mt-3 mb-0 py-2">
                <strong>Provider review is in progress.</strong> Comments and suggested edits
                are locked while this DBQ is with the provider. They will unlock after the
                provider signs or the request returns to the case manager.
              </Alert>
            ) : null}
          </div>

          <Form>
            <DbqFormView
              {...formViewProps}
              renderAfterSection={(section) => {
                if (
                  !isMusculoskeletal ||
                  !supplementalInsertAfterSectionId ||
                  section?.id !== supplementalInsertAfterSectionId
                ) {
                  return formViewProps.renderAfterSection?.(section) || null;
                }

                return (
                  <>
                    {formViewProps.renderAfterSection?.(section) || null}
                    <div ref={supplementalSectionRef}>
                      <DbqMusculoskeletalSupplementalSection
                        supplementalData={displayedMusculoskeletalSupplementalData}
                        onSupplementalChange={onSupplementalChange}
                        readOnly={!canEditMusculoskeletalSupplemental}
                        editLocked={editLocked}
                        validationErrors={supplementalValidationErrors}
                        activeReviewFieldId={activeReviewFieldId}
                        canOpenReview={canOpenReview}
                        getReviewCount={getReviewItemCount}
                        onReviewClick={onReviewClick}
                        getInlineSuggestionStateForField={getInlineSuggestionStateForField}
                        onOpenInlineSuggestionDetail={onOpenInlineSuggestionDetail}
                        onOpenInlineSuggestionEvidence={onOpenInlineSuggestionEvidence}
                        onOpenInlineGenerationFailure={onOpenInlineGenerationFailure}
                        onOpenInlineRejectedSuggestions={onOpenInlineRejectedSuggestions}
                      />
                    </div>
                  </>
                );
              }}
            />

            <div className="dbq-save-zone">
              <div className="text-muted small opacity-75">Your form is automatically saved as you type</div>
            </div>
          </Form>
        </div>

        <div className={`dbq-comments-sidebar ${showReviewPanel ? 'visible' : ''}`}>
          {showReviewPanel && reviewPanelProps ? (
            <DbqFieldReviewPanel {...reviewPanelProps} />
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default UserDbqEditorRequestFlowLayout;
